import React, { useState } from 'react';
import PageArticle from '../../components/_template/page/PageArticle';
import PageTitle from '../../components/_molecules/page/PageTitle';
import PageLoading from '../../components/_atom/loading/PageLoading';
import EmptyDataTemplate from '../dashboard/components/EmptyDataTemplate';
import BasicTable from '../../components/_atom/table/BasicTable';
import { PagingBar } from '../../components/_atom/paging/PagingBar';
import BasicButton from '../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../components/_atom/button/StyledButton';
import useLoading from '../../../hooks/useLoading';
import CopyToClipboard from 'react-copy-to-clipboard/lib';
import toast from 'react-hot-toast';
import { ReactComponent as IconCopy } from '../../assets/images/icon_copy.svg';
import arrowRight from '../../assets/images/arrow_right_btn_grey.png';
import { useNavigate } from 'react-router-dom';
import { Webhook } from '../../assets/constant/webhookManager.type';
import { logic } from '../../../logic';
import TagBadge, { TagBadgeType } from '../tokenization/components/TagBadge';
import CreateWebhookModal from '../tokenization/components/manager/webhook/CreateWebhookModal';
import KitContainer from '../tokenization/components/KitContainer';

const WEBHOOKS_API_DOCS_URL = 'https://docs.eqhub.eqbr.com/reference/overview-19';
const WEBHOOK_COLGROUP = [3, 3, 3, 1, 3];
const WEBHOOK_HEADERS = [
  { title: 'Webhook Name', accessor: 'name' },
  { title: 'ID', accessor: 'id' },
  { title: 'URL', accessor: 'url' },
  { title: 'Status', accessor: 'status' },
  { title: 'Event Type', accessor: 'type' },
];
const WEBHOOK_RENDERER = (data: Webhook) => {
  const statusText = data.is_active === 1 ? 'Active' : 'Deactivate';
  return {
    name: data.name,
    id: data.subscription_id,
    url: (
      <div style={{ display: 'flex', gap: 10, paddingRight: 10 }}>
        <div title={data.webhook_url} style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {data.webhook_url}
        </div>
        <CopyToClipboard text={data.webhook_url} onCopy={() => toast('Copied URL', { icon: '👏' })}>
          <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
        </CopyToClipboard>
      </div>
    ),
    status: <TagBadge type={data.is_active === 1 ? TagBadgeType.BLUE : TagBadgeType.GREY} text={statusText} />,
    type: data.event_name,
  };
};
const WEBHOOKS_LIMIT = 10;

const WebhookList = () => {
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [maxWebhooksNum, setMaxWebhooksNum] = useState(0);
  const getWebhooks = async (option?: { lastId?: number; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.webhook.getWebhookList({
      limit: WEBHOOKS_LIMIT,
      ...option,
    });
    if (error) {
      return;
    }
    const { data: webhooks, total: maxWebhooksNum } = result;
    setWebhooks(webhooks);
    if (!option?.offset) {
      setMaxWebhooksNum(maxWebhooksNum);
    }
  };
  const [isOpenRegisterWebhookModal, setIsOpenRegisterWebhookModal] = useState(false);
  const webhookPageNum = Number(new URLSearchParams(window.location.search).get('webhook')) || 1;
  const maxWebhooksPageNum = maxWebhooksNum % WEBHOOKS_LIMIT === 0 ? maxWebhooksNum / WEBHOOKS_LIMIT : Math.floor(maxWebhooksNum / WEBHOOKS_LIMIT) + 1;
  const { loading: getWebhooksLoading } = useLoading({
    dependency: [webhookPageNum],
    synchronousFunction: async () => {
      await getWebhooks({ offset: (webhookPageNum - 1) * WEBHOOKS_LIMIT });
    },
  });
  return (
    <PageArticle>
      <CreateWebhookModal
        isOpen={isOpenRegisterWebhookModal}
        closeModal={() => setIsOpenRegisterWebhookModal(false)}
        refreshWebhook={() => getWebhooks({ offset: (webhookPageNum - 1) * WEBHOOKS_LIMIT })}
      />
      <PageTitle title={'Webhook Manager Main'} />
      <div style={{ padding: 40 }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            marginBottom: 20,
          }}
        >
          <BasicButton theme={ButtonTheme.NORMAL} onClick={() => window.open(WEBHOOKS_API_DOCS_URL, '_blank')}>
            User Guide & API Docs
            <img src={arrowRight} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
          </BasicButton>
        </div>
        <KitContainer title={'Webhook List'}>
          {getWebhooksLoading ? (
            <PageLoading style={{ height: 477 }} />
          ) : (
            <>
              {webhooks.length === 0 ? (
                <EmptyDataTemplate>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      lineHeight: '21px',
                      color: '#7D828D',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {'There is no webhook created.\nCreate a new webhook and use Webhook Manager.'}
                  </div>
                </EmptyDataTemplate>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                  }}
                >
                  <BasicTable
                    data={webhooks}
                    colGroup={WEBHOOK_COLGROUP}
                    headers={WEBHOOK_HEADERS}
                    renderer={WEBHOOK_RENDERER}
                    onClick={(webhook: Webhook) => navigate(`/webhook/${webhook.subscription_id}`)}
                  />
                  <PagingBar maxPageNum={maxWebhooksPageNum} query={'webhook'} />
                </div>
              )}
            </>
          )}
          <div
            style={{
              display: 'flex',
              position: 'absolute',
              top: 22,
              right: 30,
            }}
          >
            <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.PRIMARY} onClick={() => setIsOpenRegisterWebhookModal(true)}>
              + New Webhook
            </BasicButton>
          </div>
        </KitContainer>
      </div>
    </PageArticle>
  );
};

export default WebhookList;
